import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useStyle from './styles';
import * as LaboratoryAction from '../../../redux/Laboratory/LaboratoryAction';
import moment from 'moment-jalaali';
import MessagesContext from '../../../contexts/messagesContext';
import LanguageContext from '../../../contexts/languageContext';
import useCheckMobileScreen from '../../../hooks/useWidth';
import { SubmitButton, CancelButton, TextInput, CalendarInput } from '../../UI';
import Item from './Item';
import { useHistory } from 'react-router-dom';
import { DateObject } from 'react-multi-date-picker';

const LabDetailForm = ({ patient_id, group_id, result_id, isNewLabResult }) => {
    const isMobile = useCheckMobileScreen();
    const { messages } = useContext(MessagesContext);
    const { lang } = useContext(LanguageContext);
    const classes = useStyle({ rtl: lang === 'fa' });
    const history = useHistory();
    const dispatch = useDispatch();
    /**
     * @type React.MutableRefObject<DatePicker>
     */

    const listRef = useRef();
    const [{ factors, values, ...detail }, setDetail] = useState({
        title: '',
        issued_datetime: moment(),
        report_datetime: undefined,
        factors: [],
        values: []
    });
    const [focusOn, setFocusOn] = useState(null);
    const labTestGroups = useSelector((store) => store.laboratory?.laboratoryTestGroups);

    const dates = useMemo(
        () => ({
            time: moment(detail.issued_datetime).format('HH:mm'),
            date: moment(detail.issued_datetime).format('jYY/jMM/jDD'),
            moment: moment(detail.issued_datetime)
        }),
        [detail]
    );

    const convertDataToUsableData = useCallback((labTests, resultId, groupId) => {
        if (!labTests?.length || !resultId || !groupId)
            return {
                values: [],
                factors: []
            };

        const group = labTests.filter((v) => `${v.id}` === groupId)[0];
        const result = group?.results?.filter((v) => `${v.id}` === `${resultId}`)[0];

        return {
            values: result.values ?? [],
            factors: group.factors ?? [],
            title: group?.title,
            ...result
        };
    }, []);

    useEffect(() => {
        setDetail({ ...convertDataToUsableData(labTestGroups, result_id, group_id) });
        handleAddNewValue();
    }, [labTestGroups, convertDataToUsableData, result_id, group_id]);

    useLayoutEffect(() => {
        const timeout = setTimeout(() => {
            listRef.current?.scrollTo && listRef.current.scrollTo(0, listRef.current.scrollHeight);
            clearTimeout(timeout);
        }, 100);
    }, [listRef]);

    const handleSaveResult = () => {
        const data = {
            issued_datetime: moment(detail.issued_datetime).toISOString(),
            patient_id,
            values: values
                .map((v) => ({ factor_id: v.factor_id, value: v.value || null }))
                .filter((v) => !`${v.factor_id}`.includes('new') && v.value)
        };

        dispatch(
            (isNewLabResult
                ? LaboratoryAction.createLaboratoryResult
                : LaboratoryAction.editLaboratoryResult)({
                data,
                result_id,
                group_id,
                patient_id,
                doOnSuccess: history.goBack
            })
        );
    };

    const handleAddNewValue = () => {
        // LaboratoryAction.createLaboratoryResult();
        const newID = `new_${Math.random() * 1000}`;
        setDetail((detail) => ({
            ...detail,
            factors: [
                ...detail.factors,
                {
                    id: newID,
                    name: '',
                    default_unit: '',
                    default_normal_range: '',
                    important: false
                }
            ],
            values: [
                ...detail.values,
                {
                    factor_id: newID,
                    value: ''
                }
            ]
        }));
    };

    const isFactorCreatedBefore = useCallback(
        (factors, factorId) => factors.map((v) => v.id).includes(factorId),
        []
    );

    const handleFactorChange = (factorIdOfValue) => (value) => {
        if (isFactorCreatedBefore(factors, value.id)) return;
        let index = values.map((v) => v.factor_id).indexOf(factorIdOfValue);
        setDetail((state) => ({
            ...state,
            factors: state.factors.map((f, i) => (i === index ? value : f)),
            values: state.values.map((v, i) => (i === index ? { ...v, factor_id: value.id } : v))
        }));
        if (`${factorIdOfValue}`.includes('new')) {
            handleAddNewValue();
        }
        setFocusOn(value.id);
    };

    const handleChange = (factorIdOfValue) => (ec) => {
        const name = ec?.target?.name;
        const value = ec?.target?.value;

        if (name === 'value') {
            setDetail((state) => ({
                ...state,
                values: state.values.map((v) =>
                    v.factor_id === factorIdOfValue ? { ...v, [name]: value } : v
                )
            }));
        }
    };

    const handleDelete = (factor_id) => () => {
        setDetail((state) => ({
            ...state,
            factors: state.factors.filter((v) => v.id !== factor_id),
            values: state.values.filter((v) => v.factor_id !== factor_id)
        }));
    };

    const handleDateChange = useCallback((date) => {
        const dateValue = new Date(date);
        const convertToMoment = moment(dateValue);
        setDetail((info) => ({ ...info, issued_datetime: convertToMoment }));
    }, []);

    // const showDatePicker = () => {
    // let timeout = setTimeout(() => {
    //     // datepicker have an issue in showing time picker and its because of zIndex: 9999999999
    //     let datePickerContainer = document.querySelector('.tether-element');
    //     if (datePickerContainer) {
    //         datePickerContainer.style.zIndex = 2000;
    //     }
    //     clearTimeout(timeout);
    // }, 100);
    // };
    const handleCancel = () => {
        history.goBack();
    };

    const _maxDate = useMemo(() => moment(), []);
    return (
        <>
            <div className={classes.container}>
                {/* <div className={isMobile && classes.mobileContainer}> */}
                <div className={classes.content} ref={listRef}>
                    <div className={classes.column}>
                        <div className={classes.dateContainer}>
                            <TextInput
                                // label={messages.date}
                                // onFocus={showDatePicker}
                                value={dates.date}
                                className={classes.inputWrapper}
                            />
                            <div className={classes.calender}>
                                <CalendarInput
                                    customClasses={classes.calenderInput}
                                    value={dates.moment}
                                    onChange={(date) => handleDateChange(new DateObject(date))}
                                    max={_maxDate}
                                    hasTimePicker
                                    usePortal
                                />
                            </div>
                            <TextInput
                                // label={messages.hour}
                                // onFocus={showDatePicker}
                                value={dates.time}
                                className={classes.inputWrapper}
                            />
                        </div>
                        <div className={classes.wrapper}>
                            {factors?.map((v, i) => (
                                <Item
                                    factor={v}
                                    factorValue={values[i]}
                                    key={`factor-${v.id}`}
                                    isNewFactor={`${v.id}`.includes('new')}
                                    handleChange={handleChange(values[i].factor_id)}
                                    handleFactorChange={handleFactorChange(values[i].factor_id)}
                                    groupId={group_id !== 'new' ? parseInt(group_id) : undefined}
                                    handleDelete={handleDelete(values[i].factor_id)}
                                    focus={`${focusOn}` === `${v.id}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className={isMobile ? classes.mobileActionsBar : classes.actionsBar}>
                    <CancelButton onClick={handleCancel} data-testid="cancelButton">
                        {messages.cancel}
                    </CancelButton>
                    <SubmitButton
                        handleClick={handleSaveResult}
                        disableSubmit
                        data-testid="saveButton"
                    >
                        {messages.save}
                    </SubmitButton>
                </div>
            </div>
        </>
    );
};
LabDetailForm.propTypes = {
    patient_id: PropTypes.string,
    group_id: PropTypes.string,
    result_id: PropTypes.any,
    isNewLabResult: PropTypes.bool,
    history: PropTypes.object
};

export default LabDetailForm;
